import React, { useState, useContext } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { mockScenarios, mockSOPs } from '../mock/mockData';
import { Play, RotateCcw, Lightbulb, BookOpen, MessageSquare } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { aiAPI } from '../services/api';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const Training = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [selectedSOP, setSelectedSOP] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [userMessage, setUserMessage] = useState('');
  const [isTraining, setIsTraining] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [finalScore, setFinalScore] = useState(null);
  const [scoringDetails, setScoringDetails] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(true);

  // Fetch training assignments on mount
  React.useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/training-assignments?status=pending`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setAssignments(data);
        }
      } catch (error) {
        console.error('Failed to fetch training assignments:', error);
      } finally {
        setLoadingAssignments(false);
      }
    };
    fetchAssignments();
  }, []);

  const startScenario = async (scenario) => {
    setSelectedScenario(scenario);
    setIsTraining(true);
    setShowResults(false);
    
    // Get initial AI customer message
    try {
      const sopText = selectedSOP ? mockSOPs.find(s => s.id === selectedSOP)?.text || '' : '';
      
      const response = await aiAPI.chatTraining({
        scenario: scenario.brief,
        mood: scenario.diffClass,
        mood_prompt: `You are a ${scenario.difficulty.toLowerCase()} customer. ${scenario.desc}`,
        mood_name: 'Customer',
        history: [],
        sop_text: sopText,
        is_opening: true
      });
      
      setChatHistory([
        {
          role: 'customer',
          content: response.reply,
          timestamp: new Date()
        }
      ]);
    } catch (error) {
      console.error('Failed to start scenario:', error);
      // Fallback to generic message if API fails
      setChatHistory([
        {
          role: 'customer',
          content: `Hi, I need help with ${scenario.desc.toLowerCase()}.`,
          timestamp: new Date()
        }
      ]);
      toast({
        title: 'Warning',
        description: 'Using fallback mode. AI may not be fully functional.',
        variant: 'destructive'
      });
    }
  };

  const sendMessage = async () => {
    if (!userMessage.trim()) return;

    // Add user message
    const newHistory = [...chatHistory, {
      role: 'agent',
      content: userMessage,
      timestamp: new Date()
    }];

    setChatHistory(newHistory);
    setUserMessage('');

    // Call real Claude API for AI response
    try {
      const sopText = selectedSOP ? mockSOPs.find(s => s.id === selectedSOP)?.text || '' : '';
      
      const response = await aiAPI.chatTraining({
        scenario: selectedScenario.brief,
        mood: selectedScenario.diffClass,
        mood_prompt: `You are a ${selectedScenario.difficulty.toLowerCase()} customer. ${selectedScenario.desc}`,
        mood_name: 'Customer',
        history: newHistory.map(msg => ({
          role: msg.role,
          content: msg.content
        })),
        sop_text: sopText,
        is_opening: false
      });
      
      setChatHistory([...newHistory, {
        role: 'customer',
        content: response.reply,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('AI chat error:', error);
      toast({
        title: 'Error',
        description: 'Failed to get AI response. Please try again.',
        variant: 'destructive'
      });
      // Fallback to previous history if API fails
      setChatHistory(newHistory);
    }
  };

  const endSession = async () => {
    try {
      const sopText = selectedSOP ? mockSOPs.find(s => s.id === selectedSOP)?.text || '' : '';
      
      // Call real AI scoring
      const result = await aiAPI.scoreTraining({
        chat_history: chatHistory.map(msg => ({
          role: msg.role,
          content: msg.content
        })),
        scenario: selectedScenario.brief,
        sop_text: sopText
      });
      
      setFinalScore(result.score);
      setScoringDetails(result);
      setShowResults(true);
      
      // Save training session to database
      try {
        await api.training.create({
          agent_id: user?.user_id || 'demo_agent',
          agent_name: user?.name || 'Demo User',
          scenario: selectedScenario.title,
          scenario_brief: selectedScenario.brief,
          difficulty: selectedScenario.difficulty,
          sop_ids: selectedSOP ? [selectedSOP] : [],
          chat_history: chatHistory.map(msg => ({
            role: msg.role,
            content: msg.content
          })),
          score: result.score,
          grade: result.grade,
          coaching: result.coaching || '',
          status: 'completed'
        });
        console.log('✅ Training session saved to database');
      } catch (dbError) {
        console.error('Failed to save training session:', dbError);
        // Don't show error to user - session still completed
      }
      
      toast({
        title: 'Training Complete!',
        description: `Your score: ${result.score}/100 - ${result.grade}`,
      });
    } catch (error) {
      console.error('Scoring error:', error);
      // Fallback to mock scoring if API fails
      const score = Math.floor(Math.random() * 30) + 70;
      setFinalScore(score);
      setScoringDetails({ score, grade: 'Good', coaching: 'Keep practicing!' });
      setShowResults(true);
      
      toast({
        title: 'Training Complete!',
        description: `Your score: ${score}/100 (Estimated)`,
      });
    }
  };

  const resetTraining = () => {
    setSelectedScenario(null);
    setChatHistory([]);
    setUserMessage('');
    setIsTraining(false);
    setShowResults(false);
    setFinalScore(null);
    setScoringDetails(null);
  };

  const getDifficultyColor = (diff) => {
    const colors = {
      easy: 'bg-green-500/10 text-green-500 border-green-500/30',
      medium: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
      hard: 'bg-red-500/10 text-red-500 border-red-500/30'
    };
    return colors[diff] || colors.easy;
  };

  if (!selectedScenario) {
    return (
      <div className="min-h-screen bg-[#0d0f12] ml-56">
        <div className="max-w-6xl mx-auto p-6">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="px-3 py-1 rounded-full bg-[#4f8ef7]/10 border border-[#4f8ef7]/30 text-[#4f8ef7] text-xs font-mono tracking-wider">
                🎯 TRAINING
              </div>
            </div>
            <h1 className="text-3xl font-semibold text-white mb-2">
              AI-Powered Chat Training
            </h1>
            <p className="text-gray-400">
              Practice realistic customer scenarios with AI-driven conversations that adapt to your responses.
            </p>
          </div>

          {/* SOP Selection */}
          <Card className="bg-[#161920] border-[#2a3040] mb-6">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#9b6dff]/10 border border-[#9b6dff]/30 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-[#9b6dff]" />
                </div>
                <div>
                  <CardTitle className="text-white text-base">Select SOP for Training</CardTitle>
                  <CardDescription className="text-gray-400 text-sm">
                    Choose which company policy to practice with
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Select value={selectedSOP} onValueChange={setSelectedSOP}>
                <SelectTrigger className="bg-[#1e2229] border-[#2a3040] text-white">
                  <SelectValue placeholder="Select an SOP..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1e2229] border-[#2a3040]">
                  {mockSOPs.map(sop => (
                    <SelectItem key={sop.id} value={sop.id} className="text-white">
                      {sop.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedSOP && (
                <div className="mt-4 p-4 bg-[#1e2229] border border-[#2a3040] rounded-lg">
                  <p className="text-sm text-gray-300 leading-relaxed">
                    {mockSOPs.find(s => s.id === selectedSOP)?.text}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recommended Training Assignments */}
          {!loadingAssignments && assignments.length > 0 && (
            <Card className="bg-[#34c78a]/5 border-[#34c78a]/30 mb-6">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="text-2xl">🎯</div>
                  <div>
                    <CardTitle className="text-white text-base">Recommended For You</CardTitle>
                    <CardDescription className="text-gray-300 text-sm">
                      Your manager assigned {assignments.length} training {assignments.length === 1 ? 'scenario' : 'scenarios'} based on your performance
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {assignments[0] && (
                    <div className="bg-[#1e2229] border border-[#34c78a]/30 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="text-sm font-medium text-white mb-1">Focus Areas:</div>
                          <div className="flex gap-2 flex-wrap">
                            {assignments[0].focus_areas.map((area, idx) => (
                              <Badge key={idx} className="bg-[#34c78a]/10 text-[#34c78a] border-[#34c78a]/30">
                                {area}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <Badge className={`${
                          assignments[0].priority === 'high' ? 'bg-red-500/10 text-red-500 border-red-500/30' :
                          assignments[0].priority === 'medium' ? 'bg-amber-500/10 text-amber-500 border-amber-500/30' :
                          'bg-blue-500/10 text-blue-500 border-blue-500/30'
                        } border`}>
                          {assignments[0].priority} priority
                        </Badge>
                      </div>
                      <div className="text-xs text-gray-400 mb-3">
                        Assigned by {assignments[0].assigned_by_name}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {assignments[0].scenarios.slice(0, 3).map((scenario, idx) => (
                          <Button
                            key={idx}
                            size="sm"
                            onClick={() => {
                              const matchedScenario = mockScenarios.find(s => 
                                s.title.toLowerCase().includes(scenario.toLowerCase()) ||
                                scenario.toLowerCase().includes(s.title.toLowerCase())
                              );
                              if (matchedScenario) {
                                startScenario(matchedScenario);
                              }
                            }}
                            className="bg-[#34c78a] hover:bg-[#34c78a]/90 text-white"
                          >
                            Start: {scenario}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Scenarios Grid */}
          <div className="mb-4">
            <h2 className="text-lg font-medium text-white mb-4">Choose a Training Scenario</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mockScenarios.map((scenario) => (
              <Card
                key={scenario.id}
                className="bg-[#161920] border-[#2a3040] hover:border-[#4f8ef7]/50 transition-all cursor-pointer group"
                onClick={() => startScenario(scenario)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="text-3xl">{scenario.icon}</div>
                    <Badge className={`${getDifficultyColor(scenario.diffClass)} border`}>
                      {scenario.difficulty}
                    </Badge>
                  </div>
                  <CardTitle className="text-white text-base group-hover:text-[#4f8ef7] transition-colors">
                    {scenario.title}
                  </CardTitle>
                  <CardDescription className="text-gray-400 text-sm">
                    {scenario.desc}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                    {scenario.brief}
                  </p>
                  <Button 
                    className="w-full bg-[#4f8ef7] hover:bg-[#4f8ef7]/90 text-white"
                    size="sm"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Training
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Training Session View
  return (
    <div className="min-h-screen bg-[#0d0f12] ml-56">
      <div className="max-w-5xl mx-auto p-6">
        {/* Training Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="text-2xl">{selectedScenario.icon}</div>
                <h1 className="text-2xl font-semibold text-white">
                  {selectedScenario.title}
                </h1>
                <Badge className={`${getDifficultyColor(selectedScenario.diffClass)} border`}>
                  {selectedScenario.difficulty}
                </Badge>
              </div>
              <p className="text-gray-400">{selectedScenario.brief}</p>
            </div>
            <Button
              variant="outline"
              onClick={resetTraining}
              className="border-[#2a3040] text-gray-400 hover:text-white"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              End Session
            </Button>
          </div>

          {/* Hints */}
          <Card className="bg-[#4f8ef7]/5 border-[#4f8ef7]/30">
            <CardContent className="pt-4">
              <div className="flex gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-[#4f8ef7] flex-shrink-0 mt-0.5" />
                <span className="text-xs font-mono tracking-wider text-[#4f8ef7] uppercase">
                  Training Hints
                </span>
              </div>
              <ul className="text-sm text-gray-300 space-y-1 ml-6">
                {selectedScenario.hints.map((hint) => (
                  <li key={hint} className="list-disc">{hint}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Chat Interface */}
        {!showResults ? (
          <Card className="bg-[#161920] border-[#2a3040]">
            <CardHeader className="border-b border-[#2a3040]">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Live Training Session
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {/* Chat Messages */}
              <div className="h-96 overflow-y-auto p-6 space-y-4">
                {chatHistory.map((msg) => (
                  <div
                    key={`${msg.role}-${msg.timestamp.getTime()}-${msg.content.substring(0, 20)}`}
                    className={`flex ${msg.role === 'agent' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg px-4 py-2.5 ${
                        msg.role === 'agent'
                          ? 'bg-[#4f8ef7] text-white'
                          : 'bg-[#1e2229] text-gray-300'
                      }`}
                    >
                      <div className="text-xs mb-1 opacity-70">
                        {msg.role === 'agent' ? 'You' : 'Customer'}
                      </div>
                      <div className="text-sm leading-relaxed">{msg.content}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Input Area */}
              <div className="border-t border-[#2a3040] p-4">
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Type your response as the agent..."
                    value={userMessage}
                    onChange={(e) => setUserMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    className="flex-1 bg-[#1e2229] border-[#2a3040] text-white resize-none"
                    rows={2}
                  />
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={sendMessage}
                      disabled={!userMessage.trim()}
                      className="bg-[#4f8ef7] hover:bg-[#4f8ef7]/90"
                    >
                      Send
                    </Button>
                    <Button
                      onClick={endSession}
                      variant="outline"
                      className="border-[#34c78a] text-[#34c78a] hover:bg-[#34c78a]/10"
                    >
                      Finish
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Results View */
          <Card className="bg-[#161920] border-[#2a3040]">
            <CardContent className="pt-8">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">🎉</div>
                <h2 className="text-2xl font-semibold text-white mb-2">
                  Training Session Complete!
                </h2>
                <p className="text-gray-400 mb-6">Great job on completing this scenario</p>
                
                <div className="inline-flex items-center gap-4 bg-[#1e2229] border border-[#2a3040] rounded-lg px-8 py-4">
                  <div>
                    <div className="text-4xl font-bold text-[#34c78a]">{finalScore}</div>
                    <div className="text-xs text-gray-400 font-mono mt-1">/ 100</div>
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium text-white">Your Score</div>
                    <div className="text-xs text-gray-400">
                      {finalScore >= 80 ? 'Excellent!' : finalScore >= 60 ? 'Good!' : 'Needs Improvement'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#4f8ef7]/5 border border-[#4f8ef7]/30 rounded-lg p-4 mb-6">
                <div className="text-xs font-mono tracking-wider text-[#4f8ef7] uppercase mb-2">
                  Coaching Notes
                </div>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {scoringDetails?.coaching || 'You demonstrated good understanding of the scenario. Continue practicing empathy and make sure to always follow company policies. Keep up the great work!'}
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={resetTraining}
                  className="flex-1 bg-[#4f8ef7] hover:bg-[#4f8ef7]/90"
                >
                  Try Another Scenario
                </Button>
                <Button
                  onClick={resetTraining}
                  variant="outline"
                  className="border-[#2a3040] text-gray-400 hover:text-white"
                >
                  Back to Training
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Training;
